import React, { forwardRef } from 'react'
import classNames from 'classnames'
import { Spacer, Field, Button, Label, Divider, Checkbox, Card, Icon, Row, Col, Collapse } from '@cleartrip/bento'
import Modal from 'lib/Modal'
import ModalHeader from '../UserProfileModalHeader'
import Dropdown from 'components/Dropdown'
import langTranslate from 'locale'
import OfflineToast from 'components/GlobalNav/UserProfile/offlineToast'

import USER_PROFILE_PAGE_CONTANTS from '../UserProfileConstants'
import { sendClevertapUIActionEvent as ClevertapEvent } from 'utils/globalNav/logPageLoadEvents'
import { ReactComponent as InfoIcon } from 'assets/icons/info-icon.svg'
import { ReactComponent as ArrowLeft } from 'assets/icons/arrow-left.svg'
import { ReactComponent as ArrowRight } from 'assets/icons/arrow-right.svg'

const isRtl = process.env.CONTENT_DIR === 'RTL'

const SignUpForm = forwardRef(
    (
        {
            animatedClass,
            toggle,
            email,
            signUpCreate,
            signUpFormError,
            errorInfo,
            accountExists,
            passwordType,
            validatePasswordHandler,
            changePasswordHandler,
            isPasswordValid,
            password,
            setPasswordType,
            setShowPasswordRules,
            showPasswordRules,
            spanClass,
            lowerCaseRegex,
            uperCaseRegex,
            oneSpecialCharacterRegex,
            numberRegex,
            confirmPasswordType,
            validateConfirmPasswordHandler,
            changeConfirmPasswordHandler,
            isConfirmPasswordValid,
            confirmPassword,
            setConfirmPasswordType,
            changeFirstNameHandler,
            isValidFirstName,
            firstName,
            validateFirstNameHandler,
            changeLastNameHandler,
            isValidLastName,
            lastName,
            validateLastNameHandler,
            toggleGender,
            gender,
            genderError,
            selectGender,
            mobileAndCountryCodeItem,
            setShowGSTIN,
            showGSTIN,
            checkBoxTextClass,
            handleLinkClick,
            gstin,
            changeGstinHandler,
            isGSTValid,
            companyName,
            changeCompanyHandler,
            isCompanyValid,
            travelCheckedStatus,
            setTravelCheckedStatus,
            showCloseButton
        },
        ref
    ) => {
        return (
            <div
                className="bg-white br-4 br-4 o-hidden"
                style={{
                    width: '400px',
                    minHeight: '456px'
                }}
            >
                <>
                    <div className="bb bc-neutral-100 px-8">
                        <ModalHeader title={langTranslate('Create a Cleartrip account')} toggle={toggle} showCloseButton={showCloseButton} />
                    </div>
                    <div
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                signUpCreate()
                            }
                        }}
                        className={animatedClass}
                    >
                        <div
                            className="oy-auto nmx-8 px-8 pt-4"
                            style={{
                                maxHeight: '340px'
                            }}
                        >
                            {signUpFormError && (
                                <>
                                    <div
                                        ref={ref}
                                        className="py-2 px-2 h-8 flex flex-middle bg-error-600 c-white fs-2 br-4"
                                    >
                                        <InfoIcon className="c-white" height={16} width={16} />
                                        <span data-testid="createAccountError" className={errorInfo}>
                                            {langTranslate('Unable to create account, please resolve the errors')}
                                        </span>
                                    </div>
                                    <Spacer className="pt-8" />
                                </>
                            )}
                            {accountExists && (
                                <>
                                    <div className="py-2 px-2 h-8 flex flex-middle bg-error-600 c-white fs-2 br-4">
                                        <InfoIcon className="c-white" height={16} width={16} />
                                        <span className={errorInfo}>
                                            {langTranslate('User already exists for the given email')}
                                        </span>
                                    </div>
                                    <Spacer className="pt-8" />
                                </>
                            )}
                            <OfflineToast page="sign up" />

                            <div className="fs-3 c-neutral-700" style={{ direction: isRtl ? 'rtl' : '' }}>
                                {langTranslate('For')} <span className="fw-600 fs-inherit c-inherit">{`${email}`}</span>
                            </div>
                            <Spacer className="pt-6" />
                            <Label className="fs-3 c-neutral-700">{langTranslate('Set a password')}</Label>
                            <Spacer className="pt-2" />
                            <div className="p-relative">
                                <Field
                                    placeholder={langTranslate('Password')}
                                    type={passwordType ? 'password' : 'text'}
                                    onBlur={validatePasswordHandler}
                                    onChange={changePasswordHandler}
                                    hasError={!isPasswordValid}
                                    value={password}
                                    onClick={() => setShowPasswordRules(true)}
                                    data-testid="passwordForm"
                                />
                                {!isPasswordValid && password.length === 0 && (
                                    <div className="c-error-500 fs-2 mt-1">
                                        {langTranslate('This field cannot be empty')}
                                    </div>
                                )}
                                <span className={spanClass} onClick={() => setPasswordType(!passwordType)}>
                                    {passwordType ? langTranslate('Show') : langTranslate('Hide')}
                                </span>
                            </div>
                            <Collapse pose={showPasswordRules ? 'open' : 'collapsed'}>
                                <Spacer className="pt-2" />
                                <Card className="px-3 py-3 bg-neutral-0 br-4">
                                    <Row>
                                        <Col span={12}>
                                            <div className="flex flex-middle">
                                                <div
                                                    style={{ width: '14px', height: '14px' }}
                                                    className="flex flex-middle flex-center"
                                                >
                                                    <Icon
                                                        data-testid="lowerCase"
                                                        icon={lowerCaseRegex.test(password) ? 'tick' : 'circle-outline'}
                                                        className={
                                                            lowerCaseRegex.test(password)
                                                                ? 'c-success-500'
                                                                : 'c-neutral-600'
                                                        }
                                                        height={lowerCaseRegex.test(password) ? 14 : 8}
                                                        width={lowerCaseRegex.test(password) ? 14 : 8}
                                                    />
                                                </div>
                                                <Spacer className="pl-1" />
                                                <span className="c-neutral-700 fs-2 ws-nowrap">
                                                    {langTranslate('One lowercase letter')}
                                                </span>
                                            </div>
                                            <Spacer className="pt-2" />
                                            <div className="flex flex-middle">
                                                <div
                                                    style={{ width: '14px', height: '14px' }}
                                                    className="flex flex-middle flex-center"
                                                >
                                                    <Icon
                                                        data-testid="uperCase"
                                                        icon={uperCaseRegex.test(password) ? 'tick' : 'circle-outline'}
                                                        className={
                                                            uperCaseRegex.test(password)
                                                                ? 'c-success-500'
                                                                : 'c-neutral-600'
                                                        }
                                                        height={uperCaseRegex.test(password) ? 14 : 8}
                                                        width={uperCaseRegex.test(password) ? 14 : 8}
                                                    />
                                                </div>
                                                <Spacer className="pl-1" />
                                                <span className="c-neutral-700 fs-2 ws-nowrap">
                                                    {langTranslate('One uppercase letter')}
                                                </span>
                                            </div>
                                            <Spacer className="pt-2" />
                                            <div className="flex flex-middle">
                                                <div
                                                    style={{ width: '14px', height: '14px' }}
                                                    className="flex flex-middle flex-center"
                                                >
                                                    <Icon
                                                        data-testid="oneNumber"
                                                        icon={numberRegex.test(password) ? 'tick' : 'circle-outline'}
                                                        className={
                                                            numberRegex.test(password)
                                                                ? 'c-success-500'
                                                                : 'c-neutral-600'
                                                        }
                                                        height={numberRegex.test(password) ? 14 : 8}
                                                        width={numberRegex.test(password) ? 14 : 8}
                                                    />
                                                </div>
                                                <Spacer className="pl-1" />
                                                <span className="c-neutral-700 fs-2 ws-nowrap">
                                                    {langTranslate('One number')}
                                                </span>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="flex flex-middle">
                                                <div
                                                    style={{ width: '14px', height: '14px' }}
                                                    className="flex flex-middle flex-center"
                                                >
                                                    <Icon
                                                        data-testid="specialCharacter"
                                                        icon={
                                                            oneSpecialCharacterRegex.test(password)
                                                                ? 'tick'
                                                                : 'circle-outline'
                                                        }
                                                        className={
                                                            oneSpecialCharacterRegex.test(password)
                                                                ? 'c-success-500'
                                                                : 'c-neutral-600'
                                                        }
                                                        height={oneSpecialCharacterRegex.test(password) ? 14 : 8}
                                                        width={oneSpecialCharacterRegex.test(password) ? 14 : 8}
                                                    />
                                                </div>
                                                <Spacer className="pl-1" />
                                                <span className="c-neutral-700 fs-2 ws-nowrap">
                                                    {langTranslate('One special character')}
                                                </span>
                                            </div>
                                            <Spacer className="pt-2" />
                                            <div className="flex flex-middle">
                                                <div
                                                    style={{ width: '14px', height: '14px' }}
                                                    className="flex flex-middle flex-center"
                                                >
                                                    <Icon
                                                        data-testid="min8"
                                                        icon={password.length >= 8 ? 'tick' : 'circle-outline'}
                                                        className={
                                                            password.length >= 8 ? 'c-success-500' : 'c-neutral-600'
                                                        }
                                                        height={password.length >= 8 ? 14 : 8}
                                                        width={password.length >= 8 ? 14 : 8}
                                                    />
                                                </div>
                                                <Spacer className="pl-1" />
                                                <span className="c-neutral-700 fs-2 ws-nowrap">
                                                    {langTranslate('8 characters minimum')}
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            </Collapse>
                            <Spacer className="pt-4" />
                            <div className="p-relative">
                                <Field
                                    placeholder={langTranslate('Confirm password')}
                                    type={confirmPasswordType ? 'password' : 'text'}
                                    onBlur={validateConfirmPasswordHandler}
                                    onChange={changeConfirmPasswordHandler}
                                    hasError={!isConfirmPasswordValid}
                                    value={confirmPassword}
                                    data-testid={'passwordForm1'}
                                />
                                <span
                                    className={spanClass}
                                    onClick={() => setConfirmPasswordType(!confirmPasswordType)}
                                >
                                    {confirmPasswordType ? langTranslate('Show') : langTranslate('Hide')}
                                </span>
                            </div>
                            {!isConfirmPasswordValid && confirmPassword.length === 0 && (
                                <div className="c-error-500 fs-2 mt-1">
                                    {langTranslate('This field cannot be empty')}
                                </div>
                            )}

                            {!isConfirmPasswordValid && confirmPassword.length > 0 && (
                                <div className="c-error-500 fs-2 mt-1">
                                    {langTranslate('Please make sure your passwords match')}
                                </div>
                            )}

                            <Spacer className="pt-8" />
                            <Label className="fs-3 c-neutral-700">{langTranslate('Your name')}</Label>
                            <Spacer className="pt-2" />
                            <Field
                                placeholder={langTranslate('First name')}
                                onChange={changeFirstNameHandler}
                                hasError={!isValidFirstName}
                                value={firstName}
                                onBlur={validateFirstNameHandler}
                                data-testid="firstName"
                            />

                            {!isValidFirstName && firstName.length === 0 && (
                                <div data-testid="firstNameFieldEmpty" className="c-error-500 fs-2 mt-1">
                                    {langTranslate('This field cannot be empty')}
                                </div>
                            )}

                            {!isValidFirstName && firstName.length > 0 && (
                                <div data-testid="firstNameFieldInvalid" className="c-error-500 fs-2 mt-1">
                                    {langTranslate('Please enter a valid first name')}
                                </div>
                            )}

                            <Spacer className="pt-4" />
                            <div className="flex">
                                <div className="p-relative flex-1">
                                    <Field
                                        onChange={changeLastNameHandler}
                                        hasError={!isValidLastName}
                                        value={lastName}
                                        onBlur={validateLastNameHandler}
                                        placeholder={langTranslate('Last name')}
                                        data-testid="lastName"
                                    />
                                    {!isValidLastName && lastName.length === 0 && (
                                        <div data-testid="lastNameFieldEmpty" className="c-error-500 fs-2 mt-1">
                                            {langTranslate('This field cannot be empty')}
                                        </div>
                                    )}

                                    {!isValidLastName && lastName.length > 0 && (
                                        <div data-testid="lastNameFieldInvalid" className="c-error-500 fs-2 mt-1">
                                            {langTranslate('Please enter a valid last name')}
                                        </div>
                                    )}
                                </div>
                                <Spacer className="pr-2" />
                                <div className="p-relative">
                                    <Dropdown overlay={false} toggle={toggleGender}>
                                        <Dropdown.Select
                                            position={isRtl ? 'right' : 'left'}
                                            hasError={false}
                                            className="fs-3 h-9"
                                            width={80}
                                            placeHolder={langTranslate('Gender')}
                                            label={langTranslate(gender)}
                                            hasError={genderError}
                                        />
                                        <Dropdown.Menu width={112}>
                                            <Dropdown.Item
                                                className=" fs-3  hover:bg-secondary-500 c-pointer hover:c-white"
                                                onClick={e => selectGender(e)}
                                            >
                                                {langTranslate('Male')}
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                className="fs-3  hover:bg-secondary-500 c-pointer hover:c-white"
                                                onClick={e => selectGender(e)}
                                            >
                                                {langTranslate('Female')}
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    {genderError && (
                                        <div className="c-error-500 fs-2 mt-1">
                                            {langTranslate('Please choose gender')}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Spacer className="pt-8" />
                            <Label className="fs-3 c-neutral-700">{langTranslate('Mobile number')}</Label>
                            <Spacer className="pt-2" />
                            {mobileAndCountryCodeItem}
                            <Spacer className="pt-8" />
                            <div>
                                <Checkbox
                                    data-testid="checkboxGst"
                                    wrapperClassName="nml-1"
                                    onClick={() => setShowGSTIN(!showGSTIN)}
                                >
                                    <span className={isRtl ? checkBoxTextClass : 'pl-2 c-neutral-700  fs-2 nl-1 nmb-2'}>
                                        {langTranslate('Link my GSTIN. Unlock exclusive benefits on all bookings.')}
                                        <span
                                            className="pl-1 c-secondary-500 fs-inherit hover:td-underline c-pointer"
                                            onClick={() => handleLinkClick('cleartripforwork')}
                                        >
                                            {langTranslate('Know more')}
                                        </span>
                                    </span>
                                </Checkbox>
                                <Spacer className="pt-2" />
                            </div>

                            <Spacer className="pt-3" />
                            <Collapse pose={showGSTIN ? 'open' : 'collapsed'}>
                                <Label className="fs-3 c-neutral-700">{langTranslate('GSTIN and company name')}</Label>
                                <Spacer className="pt-2" />
                                <div className="flex">
                                    <Field
                                        data-testid="gst"
                                        placeholder={langTranslate('GSTIN number')}
                                        value={gstin}
                                        onChange={e => changeGstinHandler(e)}
                                        hasError={!isGSTValid}
                                    />

                                    <Spacer className="pr-2" />
                                    <Field
                                        data-testid="company"
                                        placeholder={langTranslate('Company name')}
                                        value={companyName}
                                        onChange={e => changeCompanyHandler(e)}
                                        hasError={!isCompanyValid}
                                    />
                                </div>
                                {!isGSTValid && (
                                    <div className="c-error-500 fs-2 mt-1">
                                        {langTranslate('Please enter a valid GSTIN')}
                                    </div>
                                )}

                                {!isCompanyValid && (
                                    <div className="c-error-500 fs-2 mt-1">
                                        {langTranslate('Please enter a valid company name')}
                                    </div>
                                )}
                                <Spacer className="pt-8" />
                            </Collapse>
                            {/* <div>
                                <Checkbox
                                    wrapperClassName="nml-1"
                                    checked={travelCheckedStatus}
                                    onClick={() => setTravelCheckedStatus(!travelCheckedStatus)}
                                >
                                    <span className={isRtl ? checkBoxTextClass : 'pl-2 c-neutral-700  fs-2 nl-1'}>
                                        {langTranslate('Send me travel offers, deals, and news by email and message')}
                                    </span>
                                </Checkbox>
                            </div> */}
                            <Spacer className="pt-8" />
                            <Divider />
                            <Spacer className="pt-5" />
                            <div
                                className="flex flex-start flex-bottom flex-center"
                                style={{ direction: isRtl ? 'rtl' : '' }}
                            >
                                <span className="c-neutral-700 fs-2">{langTranslate('Read Cleartrip’s')}</span>
                                <span
                                    className="c-secondary-500 fw-500 fs-2 hover:td-underline c-pointer"
                                    onClick={() => handleLinkClick('privacy-policy')}
                                >
                                    &nbsp;{langTranslate('privacy policy')}
                                </span>
                                <span className="c-neutral-700 fs-2">&nbsp;&</span>
                                <span
                                    className="c-secondary-500 fw-500 fs-2 hover:td-underline c-pointer"
                                    onClick={() => handleLinkClick('terms-of-service')}
                                >
                                    &nbsp;{langTranslate('terms of use')}
                                </span>
                            </div>
                            <Spacer className="pt-5" />
                        </div>
                        <div
                            className="nmx-8 elevation-3 ox-hidden flex flex-middle flex-between px-8 "
                            style={{
                                height: '68px',
                                boxShadow: '0 0 10px 1px rgba(0,0,0,0.1)'
                            }}
                        >
                            <Button size="md" type="secondary" onClick={() => signUpCreate()} full>
                                <span className="fs-3 fw-550"> {langTranslate('Create account')}</span>
                            </Button>
                        </div>
                    </div>
                </>
            </div>
        )
    }
)

export default SignUpForm
